import './index.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ChatHistoryProvider } from './hooks/useChatHistory';
import { VaultProvider } from './hooks/useVaults';
import { Layout } from './components/Layout/Layout';
import { LoginPage } from './pages/Login';
import { ChatPage } from './pages/Chat';
import { DocumentsPage } from './pages/Documents';
import { DashboardPage } from './pages/Dashboard';
import { ExtractorsPage } from './pages/Extractors';
import { BillingUsage } from './pages/BillingUsage';
import { MyTeam } from './pages/MyTeam';
import { BillingPlans } from './pages/Admin/BillingPlans';
import { UsersAndQuota } from './pages/Admin/UsersAndQuota';
import { VoiceSettings } from './pages/Admin/VoiceSettings';
import { AiProxySettings } from './pages/Admin/AiProxySettings';
import { ProfilePage } from './pages/Profile';
import { OperationsPage } from './pages/Operations';
import { GrowthPage } from './pages/Growth';
import { TrainingPage } from './pages/Training';
import { VaultBotsPage } from './pages/VaultBots';
import { PublicShare } from './pages/PublicShare';
import { PublicVerifyPage } from './pages/PublicVerify';
import { KnowledgeGraphPage } from './pages/KnowledgeGraph';
import { WorkspacePage } from './pages/Workspace';
import { CollectiveFeedPage } from './pages/CollectiveFeed';
import { TrustedAgentsPage } from './pages/TrustedAgents';
import { ExpertiseScorePage } from './pages/ExpertiseScore';
import { MarketplacePage } from './pages/Marketplace';
import { LegacyPage } from './pages/Legacy';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '16px', background: '#080816', color: '#f1f5f9' }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang kết nối tới ErixOne...</span>
    </div>
  );
  return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <ChatHistoryProvider>
        <VaultProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/share/:shareId" element={<PublicShare />} />
              <Route path="/verify/:certificateId" element={<PublicVerifyPage />} />
              <Route path="/" element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }>
                <Route index element={<Navigate to="/chat" replace />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="extractors" element={<ExtractorsPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="billing" element={<BillingUsage />} />
                <Route path="team" element={<MyTeam />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="operations" element={<OperationsPage />} />
                <Route path="growth" element={<GrowthPage />} />
                <Route path="training" element={<TrainingPage />} />
                <Route path="bots" element={<VaultBotsPage />} />
                <Route path="graph" element={<KnowledgeGraphPage />} />
                <Route path="workspace" element={<WorkspacePage />} />
                <Route path="collective" element={<CollectiveFeedPage />} />
                <Route path="agents" element={<TrustedAgentsPage />} />
                <Route path="expertise" element={<ExpertiseScorePage />} />
                <Route path="marketplace" element={<MarketplacePage />} />
                <Route path="legacy" element={<LegacyPage />} />
                <Route path="admin/plans" element={<BillingPlans />} />
                <Route path="admin/users" element={<UsersAndQuota />} />
                <Route path="admin/voice" element={<VoiceSettings />} />
                <Route path="admin/ai-proxy" element={<AiProxySettings />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </VaultProvider>
      </ChatHistoryProvider>
    </AuthProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
